import { rem } from "@certn/cascadia";
import { Divider, GridItem, Loader, Text } from "@certn/cascadia-react";
import { usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useField } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CategoryType, DocumentField } from "./DocumentField";
import { PrimaryInfoModal } from "./PrimaryInfoModal";
import { SecondaryInfoModal } from "./SecondaryInfoModal";

export function AustralianNpcIdentificationDocumentsInputField({
  field,
}: {
  field: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"];
}) {
  const { t } = useTranslation();
  const [, meta, helpers] = useField(field.name);
  const [files, setFiles] = useState<
    Record<
      CategoryType,
      components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"]["value"]
    >
  >({
    primary: field.value.filter(
      ({ document_subtype }) => document_subtype === "primary"
    ),
    secondary: field.value.filter(
      ({ document_subtype }) => document_subtype === "secondary"
    ),
  });

  const { isLoading: documentTypesLoading } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<CategoryType, { key: string; value: string }[]>
  >("/internal/people/document-types/", {
    check: "npc",
  });

  useEffect(() => {
    // update Formik values when change happens
    helpers.setValue(Object.values(files).reduce((i, j) => [...i, ...j], []));
  }, [files]);

  const updateFiles = (
    key: CategoryType,
    files: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"]["value"],
    first: boolean
  ) => {
    // If "first" is true, we replace files[0] in state, else we keep files[0] and overwrite the rest
    setFiles((prev) => ({
      ...prev,
      [key]: first
        ? [...files, ...prev[key].slice(1)]
        : [...prev[key].slice(0, 1), ...files],
    }));
  };

  if (documentTypesLoading) {
    // need to wait for types to load before displaying files
    // removing this will break what "subtitle" for each file displays
    return (
      <GridItem>
        <Loader />
      </GridItem>
    );
  }

  return (
    <>
      <GridItem>
        <Divider />
      </GridItem>

      <GridItem className={css({ gap: rem("space-2") })}>
        <Text style="form/section-heading">
          <Trans>Required documents</Trans>
        </Text>
        <Text style="form/body-secondary" color="text-weak">
          <Trans>File types accepted: PDF, JPG or PNG (max 10 MB)</Trans>
        </Text>
      </GridItem>

      <DocumentField
        type="primary"
        label={t("Primary document")}
        info={<PrimaryInfoModal />}
        files={files.primary.slice(0, 1)}
        isRequired
        onChange={(files) => updateFiles("primary", files, true)}
        errorMessage={
          meta.touched && meta.error === "MISSING_PRIMARY_DOCUMENT"
            ? t("Primary document is required")
            : undefined
        }
      />

      <DocumentField
        type="primary"
        label={t("Primary document")}
        info={<PrimaryInfoModal />}
        files={files.primary.slice(1)}
        onChange={(files) => updateFiles("primary", files, false)}
      />

      <DocumentField
        type="secondary"
        label={t("Secondary document")}
        info={<SecondaryInfoModal />}
        files={files.secondary.slice(0, 1)}
        onChange={(files) => updateFiles("secondary", files, true)}
      />

      <DocumentField
        type="secondary"
        label={t("Secondary document")}
        info={<SecondaryInfoModal />}
        files={files.secondary.slice(1)}
        onChange={(files) => updateFiles("secondary", files, false)}
      />
    </>
  );
}
